import React from 'react'
import PropTypes from 'prop-types'
import { useViewport } from '@chilecompra/react-kit'
import { Card, Skeleton, Typography } from '@chilecompra/react-kit/components'
import SummaryBidProducts from './SummaryBidProducts'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { Divider, GridContainer, GridItem } from './SummaryBid.styles'

/**
 * The SummaryBid's component.
 */
const SummaryBid = props => {
  const {
    amount,
    businessName,
    detail,
    dispatchValue,
    effectiveDate,
    loading,
    netWorth,
    products,
    sendDate,
    taxation,
    taxationValue,
    typeCurrency,
    downloadAction
  } = props
  const { size } = useViewport()

  const title =
    businessName !== '' && businessName !== null ? `Cotización enviada por ${businessName}` : 'Cotización enviada'

  return (
    <>
      <GridContainer margin="24px 0 0">
        <GridItem sm={6} xs={12}>
          <Typography color="black3" fontSize="12px" fontWeight="bold" lineHeight="16px" variant="caption">
            {loading ? <Skeleton /> : `FECHA DE ENVÍO ${sendDate}`}
          </Typography>
        </GridItem>
        <GridItem sm={6} xs={12}>
          <Typography
            align={size.isDownToTiny ? 'left' : 'right'}
            color="black3"
            fontSize="12px"
            fontWeight="bold"
            lineHeight="16px"
            variant="caption"
          >
            {loading ? <Skeleton /> : `vigencia ${effectiveDate}`}
          </Typography>
        </GridItem>
        <GridItem xs={12}>
          <Typography color="black1" fontSize="22px" fontWeight="bold" lineHeight="26px" variant="h3">
            {loading ? <Skeleton /> : title}
          </Typography>
        </GridItem>
        <GridItem xs={12}>
          <Typography color="black1" fontSize="14px" lineHeight="18px" margin="12px 0 32px" variant="body2">
            {loading ? <Skeleton /> : detail}
          </Typography>
        </GridItem>
      </GridContainer>
      <GridContainer spacing={2}>
        <GridItem md={9} xs={12}>
          {products !== undefined &&
            Object.values(products).map(product => (
              <SummaryBidProducts
                amount={product.amount}
                description={product.description}
                productCode={product.productCode}
                key={product.id}
                loading={loading}
                name={product.name}
                quantity={product.quantity}
                typeCurrency={typeCurrency}
                unit={product.unit}
                unitPrice={product.unitPrice}
              />
            ))}
          <Card color="blank" enableBorder margin="4px 0 0">
            <GridContainer>
              <GridItem md={7} xs={12} />
              <GridItem align={size.isDownToTiny ? 'left' : 'right'} md={3} xs={12}>
                <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                  Valor de despacho
                </Typography>
              </GridItem>
              <GridItem align={size.isDownToTiny ? 'left' : 'right'} md={2} xs={12}>
                <Typography
                  color={dispatchValue ? 'black1' : 'black2'}
                  fontSize="14px"
                  fontWeight="bold"
                  lineHeight="18px"
                  variant="body2"
                >
                  {loading ? (
                    <Skeleton />
                  ) : dispatchValue ? (
                    formatTypeCurrency(typeCurrency, dispatchValue, true)
                  ) : (
                    'Sin información'
                  )}
                </Typography>
              </GridItem>
            </GridContainer>
          </Card>
        </GridItem>
        <GridItem md={3} xs={12}>
          <Card enableBorder color="primary">
            <GridContainer margin="-8px" padding="0 0 44px" spacing={2}>
              <GridItem xs={12} sm={7}>
                <Typography
                  align={size.isDownToTiny ? 'left' : 'right'}
                  color="black2"
                  fontSize="14px"
                  lineHeight="18px"
                  variant="body2"
                >
                  Valor neto
                </Typography>
              </GridItem>
              <GridItem sm={5} xs={12}>
                <Typography
                  align={size.isDownToTiny ? 'left' : 'right'}
                  color="black1"
                  fontSize="14px"
                  fontWeight="bold"
                  lineHeight="18px"
                  variant="body2"
                >
                  {loading ? <Skeleton /> : formatTypeCurrency(typeCurrency, netWorth, true)}
                </Typography>
              </GridItem>
              <GridItem sm={7} xs={12}>
                <Typography
                  align={size.isDownToTiny ? 'left' : 'right'}
                  color="black2"
                  fontSize="14px"
                  lineHeight="18px"
                  variant="body2"
                >
                  {loading ? <Skeleton /> : taxation}
                </Typography>
              </GridItem>
              <GridItem sm={5} xs={12}>
                <Typography
                  align={size.isDownToTiny ? 'left' : 'right'}
                  color="black1"
                  fontSize="14px"
                  fontWeight="bold"
                  lineHeight="18px"
                  variant="body2"
                >
                  {loading ? <Skeleton /> : formatTypeCurrency(typeCurrency, taxationValue, true)}
                </Typography>
              </GridItem>
            </GridContainer>
            <Divider />
            <GridContainer padding="12px 0 0" width="100%">
              <GridItem align={size.isDownToTiny ? 'left' : 'right'} width="100%">
                <Typography
                  align={size.isDownToTiny ? 'left' : 'right'}
                  color="black3"
                  fontSize="16px"
                  lineHeight="20px"
                  variant="body1"
                >
                  Monto total
                </Typography>
              </GridItem>
            </GridContainer>
            <GridContainer width="100%">
              <GridItem align={size.isDownToTiny ? 'left' : 'right'} width="100%">
                <Typography
                  align={size.isDownToTiny ? 'left' : 'right'}
                  color="black1"
                  fontSize="22px"
                  fontWeight="bold"
                  lineHeight="26px"
                  variant="body1"
                >
                  {loading ? <Skeleton /> : formatTypeCurrency(typeCurrency, amount, true)}
                </Typography>
              </GridItem>
            </GridContainer>
          </Card>
          {downloadAction}
        </GridItem>
      </GridContainer>
    </>
  )
}

SummaryBid.defaultProps = {
  amount: 0,
  businessName: '',
  detail: '',
  dispatchValue: 0,
  downloadAction: null,
  effectiveDate: '',
  loading: false,
  netWorth: 0,
  products: [],
  sendDate: '',
  taxation: '',
  taxationValue: 0,
  typeCurrency: 'CLP'
}

SummaryBid.propTypes = {
  amount: PropTypes.number,
  businessName: PropTypes.string,
  detail: PropTypes.string,
  dispatchValue: PropTypes.number,
  downloadAction: PropTypes.node,
  effectiveDate: PropTypes.string,
  loading: PropTypes.bool,
  netWorth: PropTypes.number,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      unit: PropTypes.string.isRequired,
      unitPrice: PropTypes.number.isRequired
    })
  ),
  sendDate: PropTypes.string,
  taxation: PropTypes.string,
  taxationValue: PropTypes.number,
  typeCurrency: PropTypes.string
}

export default SummaryBid
